import { useEffect } from "react";

import { IgSelect, IgTypography } from "@intelligems/ig-theme";
import { AnalyzeTinybirdResourceResponseBodyType } from "@intelligems/ig-types";
import { GraphConfig } from "@intelligems/ig-types";
import { MenuItem, Skeleton, Stack } from "@mui/material";
import { selectData } from "@src/api/analytics/dashboard-tinybird";
import { useAnalyticsFetching } from "@src/api/analytics/hooks/use-analytics-fetching";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { BaseGraphPaper } from "@src/features/analytics/baseDashboard/chartComponents/GraphPapers";
import { GraphsWrapper } from "@src/features/analytics/baseDashboard/chartComponents/GraphsWrapper";
import { CompareGroupsDrawerContent } from "@src/features/mobileAnalytics/drawers/CompareGroupsDrawerContent";
import { useMobileDrawer } from "@src/features/mobileAnalytics/shared/hooks/useMobileDrawer";
import { DrawerTypes } from "@src/features/mobileAnalytics/shared/providers/types";
import { useSelector } from "@src/store";
import { getExperienceAnalyticsFilters } from "@src/store/slices/dashboardFilters";
import { selectedExperience } from "@src/store/slices/experiences";
import { selectedVariations } from "@src/store/slices/variations";
import { UseQueryResult } from "@tanstack/react-query";

type Props = {
  data: UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[];
  totalExperienceVisitors: number;
  graphConfigs: GraphConfig[];
  testDuration: number;
  compareVariationId: string;
};

export const KeyMetrics = ({
  data,
  totalExperienceVisitors,
  graphConfigs,
  testDuration,
  compareVariationId,
}: Props) => {
  const experience = useSelector(selectedExperience);
  const variations = useSelector(selectedVariations);
  const dashboardFilters = useSelector(getExperienceAnalyticsFilters);
  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");
  const onMetricDetailsClick = () => {};
  const isAnalyticsFetching = useAnalyticsFetching();
  const drawer = useMobileDrawer();

  // Register drawer on mount
  useEffect(() => {
    drawer.registerDrawer({
      type: DrawerTypes.COMPARE_GROUPS,
      header: "Compare Groups",
      content: <CompareGroupsDrawerContent />,
    });
  }, []);

  const groupAVariation = variations.find(
    (v) => v.id === dashboardFilters.compareGroupA
  );
  const groupBVariation = variations.find(
    (v) => v.id === dashboardFilters.compareGroupB
  );
  const groupAName =
    groupAVariation?.name ?? dashboardFilters.compareGroupA ?? "";
  const groupBName =
    groupBVariation?.name ?? dashboardFilters.compareGroupB ?? "";

  const comparisonText = `${groupAName} vs ${groupBName}`;

  return (
    <Stack width={"100%"} spacing={3}>
      <IgTypography variant="h3" fontWeight={600}>
        Key Metrics
      </IgTypography>
      {isAnalyticsFetching ? (
        <Skeleton variant="rectangular" width="100%" height={24} />
      ) : (
        <IgSelect
          open={false}
          sx={{
            "& .MuiSelect-select": {
              paddingX: 4,
              paddingY: 2,
              marginRight: 4,
            },
          }}
          fullWidth={true}
          value={comparisonText}
          onOpen={() => drawer.openDrawer(DrawerTypes.COMPARE_GROUPS)}
          onChange={() => {}}
        >
          <MenuItem value={comparisonText}>
            <IgTypography>{comparisonText}</IgTypography>
          </MenuItem>
        </IgSelect>
      )}

      {graphConfigs.map((config, index) => {
        return (
          <BaseGraphPaper key={index}>
            <GraphsWrapper
              isDataLoading={isAnalyticsFetching > 0}
              totalTestVisitors={totalExperienceVisitors}
              config={config}
              onMetricDetailsClick={onMetricDetailsClick}
              testDuration={testDuration}
              renderLocation={"experiment"}
              data={selectData({
                dashboardData: data,
                config,
                _resource: experience,
                locale: storeCurrencyConfig?.value?.locale,
                metricFilterIndex: 0,
                variations,
              })}
              rawData={data}
              compareVariationId={compareVariationId}
            />
          </BaseGraphPaper>
        );
      })}
    </Stack>
  );
};
